/**
 * TJR's defaults file `tjf.dat` and the aircraft of TJR job files (`Report_Log_*.tjf`), read for the staff and
 * aircraft imports of Teslyn Cloud (MB's R78, R79; docs/specs/cloud/tjr-register-import.md).
 *
 * Both are TJR "NeWe" containers (`c:\Apps\TJR\tjr decompile\docs\FUNCTIONAL_SPEC.md` §7, `TJF_FORMAT.md` §1; the
 * Rust reader of `.tjf` jobs is crates/teslyn-core/src/tjf/codec.rs): every byte XOR 0x55; after that, Windows-1252
 * text `NeWe` CR `<payload length>` CR, then CRLF-separated records, each a three-digit code and `~`-separated
 * fields. `tjf.dat`, TJR's defaults database, holds:
 *
 *   000  pilots        `Y|N ~ name ~ licence ~ phone ~`
 *   001  operators     `Y|N ~ name ~ short name ~ phone ~`
 *   002  aircraft types (no registration): `idx ~ ~ fuel flow ~ MR interval ~ ~ ~ ~ max flight ~ fuel type ~ brand ~
 *        model ~ ~ cycles ~ engines ~ cruise ~ stall ~ engine type ~ wing ~ corner image ~ image ~ ~`
 *   003  managers      `name ~ phone ~ phone ~`
 *   004  company phones
 *   005  registrations `registration ~ sat phone ~ BEW|MTOW|max fuel|L/h|airspeed|reserve|tip tank| ~`
 *   006  standard hazards
 *
 * A job's aircraft are its 012 records (TJF_IMPORT_EXPORT_SPEC.md §3.2.1), which carry the type's fields too.
 *
 * Only what the imports need is kept: names, the TJR current flag and roles of people; registrations, types and the
 * aircraft figures. Phone numbers are dropped as they are read. `ncom.dat` (TJR's update server and its sign-in) and
 * `pntqtstgs.dat` (quote and contract paragraphs) are the same container and hold neither staff nor aircraft.
 */
import type { FuelType, RegisterRole } from './model.js';

export const TJR_XOR_KEY = 0x55;
const MAGIC = 'NeWe';

/** Why a file is not a TJR container. */
export class TjrFileError extends Error {}

/** Decode a TJR container into its records (code and fields), in file order. */
export function readTjrContainer(bytes: Uint8Array): TjrRecord[] {
  const plain = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) plain[i] = bytes[i]! ^ TJR_XOR_KEY;
  const head = String.fromCharCode(...plain.subarray(0, Math.min(plain.length, 24)));
  const m = /^NeWe\r(\d+)\r/.exec(head);
  if (!head.startsWith(MAGIC) || !m) {
    throw new TjrFileError(
      'This is not a list file or a project file. Choose tjf.dat or a Report_Log_*.tjf file.',
    );
  }
  const start = m[0].length;
  const declared = Number(m[1]);
  if (plain.length - start !== declared) {
    throw new TjrFileError(
      `This file is cut short or changed: it says ${declared} bytes, it holds ${plain.length - start}.`,
    );
  }
  const text = new TextDecoder('windows-1252').decode(plain.subarray(start));
  const out: TjrRecord[] = [];
  for (const line of text.split('\r\n')) {
    if (line === '' || line.startsWith('//')) continue;
    out.push({ code: line.slice(0, 3), fields: line.slice(3).split('~') });
  }
  return out;
}

export interface TjrRecord {
  readonly code: string;
  readonly fields: readonly string[];
}

/** A person of TJR's crew lists, with the role their list gives. */
export interface TjrPerson {
  readonly name: string;
  readonly role: Extract<RegisterRole, 'pilot' | 'operator' | 'manager'>;
  /**
   * TJR's own flag on the pilot and operator lists (`Y` or `N`); null for managers, whose list has none. TJR's
   * screens do not show it; the people marked `Y` are those still flying with Thomson, so it is read as "current".
   */
  readonly current: boolean | null;
  /**
   * The person's phone number as TJR keeps it ("0412 345 678"), or null (decision 137: crew phone numbers are
   * imported and every staff role reads them). A manager's first number.
   */
  readonly phone: string | null;
}

/** An aircraft type of TJR's defaults (002): what TJR copies onto a job's aircraft when it is added. */
export interface TjrAircraftType {
  readonly brand: string;
  readonly model: string;
  readonly fuelFlowLph: number | null;
  readonly mrIntervalH: number | null;
  readonly maxFlightH: number | null;
  readonly fuelType: FuelType | null;
  readonly engines: number | null;
  readonly engineType: string;
  readonly helicopter: boolean;
  readonly cruiseKts: number | null;
  readonly stallKts: number | null;
}

/** A registration of TJR's defaults (005) with its job safety analysis figures; TJR stores no type with it. */
export interface TjrRegistration {
  readonly registration: string;
  /** Basic empty weight and maximum take-off weight, kg. */
  readonly bewKg: number | null;
  readonly mtowKg: number | null;
  /** Maximum fuel, L; fuel burn, L/h; average airspeed, kt; reserve fuel, min. */
  readonly maxFuelL: number | null;
  readonly fuelLph: number | null;
  readonly airspeedKts: number | null;
  readonly reserveMin: number | null;
  /** Tip tanks fitted (TJR's `T`). */
  readonly tipTanks: boolean;
}

export interface TjrDefaults {
  readonly people: readonly TjrPerson[];
  readonly aircraftTypes: readonly TjrAircraftType[];
  readonly registrations: readonly TjrRegistration[];
}

const num = (s: string | undefined): number | null => {
  const t = (s ?? '').trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/** TJR's fuel type: 0 avgas, 1 turbine (as Teslyn Field's TJR import reads it); anything else unknown. */
const positive = (n: number | null): number | null => (n !== null && n > 0 ? n : null);

const fuelType = (s: string | undefined): FuelType | null =>
  s?.trim() === '0' ? 'avgas' : s?.trim() === '1' ? 'turbine' : null;

const flag = (s: string | undefined): boolean | null => (s === 'Y' ? true : s === 'N' ? false : null);

/** A phone field as TJR wrote it, spaces tidied; null when empty or not a phone number. */
const phoneOf = (s: string | undefined): string | null => {
  const t = (s ?? '').trim().replace(/\s+/g, ' ');
  return /^\+?[\d ()-]{6,}$/.test(t) && /\d{3}/.test(t.replace(/\D/g, '')) ? t : null;
};

/** Read TJR's `tjf.dat`: its crew lists, aircraft types and registrations. */
export function readTjrDefaults(bytes: Uint8Array): TjrDefaults {
  const records = readTjrContainer(bytes);
  // pntqtstgs.dat numbers its paragraphs 000 to 067 too: a crew record starts with TJR's Y or N flag, a type
  // record has its 21 fields
  const crewOrTypes = records.some(
    (r) =>
      ((r.code === '000' || r.code === '001') && /^[YN]$/.test(r.fields[0] ?? '') && r.fields.length >= 4) ||
      (r.code === '002' && r.fields.length >= 20),
  );
  if (!crewOrTypes) {
    throw new TjrFileError(
      'This file holds no staff or aircraft lists. They are in tjf.dat, in the previous reporting program’s folder.',
    );
  }
  const people: TjrPerson[] = [];
  const aircraftTypes: TjrAircraftType[] = [];
  const registrations: TjrRegistration[] = [];
  for (const { code, fields: f } of records) {
    if (code === '000' || code === '001') {
      const name = (f[1] ?? '').trim();
      if (name)
        people.push({
          name,
          role: code === '000' ? 'pilot' : 'operator',
          current: flag(f[0]),
          phone: phoneOf(f[3]),
        });
    } else if (code === '003') {
      const name = (f[0] ?? '').trim();
      if (name) people.push({ name, role: 'manager', current: null, phone: phoneOf(f[1]) ?? phoneOf(f[2]) });
    } else if (code === '002') {
      aircraftTypes.push(typeFromFields(f));
    } else if (code === '005') {
      const registration = (f[0] ?? '').trim().toUpperCase();
      if (!registration) continue;
      const p = (f[2] ?? '').split('|');
      registrations.push({
        registration,
        bewKg: num(p[0]),
        mtowKg: num(p[1]),
        maxFuelL: num(p[2]),
        fuelLph: num(p[3]),
        airspeedKts: num(p[4]),
        reserveMin: num(p[5]),
        tipTanks: p[6]?.trim().toUpperCase() === 'T',
      });
    }
  }
  return { people, aircraftTypes, registrations };
}

/** The type fields of a 002 (defaults) or 012 (job) record; both share the column order from field 2 on. */
function typeFromFields(f: readonly string[]): TjrAircraftType {
  return {
    brand: (f[9] ?? '').trim(),
    model: (f[10] ?? '').trim(),
    fuelFlowLph: num(f[2]),
    mrIntervalH: num(f[3]),
    maxFlightH: positive(num(f[7])),
    fuelType: fuelType(f[8]),
    engines: num(f[13]),
    engineType: (f[16] ?? '').trim(),
    helicopter: (f[17] ?? '').trim() === '1',
    cruiseKts: num(f[14]),
    stallKts: num(f[15]),
  };
}

/** An aircraft of a TJR job (012): its registration and type, as TJR copied them from its defaults. */
export interface TjrJobAircraft extends TjrAircraftType {
  readonly registration: string;
}

/** The aircraft of a TJR job file (`Report_Log_*.tjf`). */
export function readTjrJobAircraft(bytes: Uint8Array): TjrJobAircraft[] {
  const records = readTjrContainer(bytes);
  if (!records.some((r) => r.code === '013')) {
    throw new TjrFileError('This file is not a project file (Report_Log_*.tjf).');
  }
  const out: TjrJobAircraft[] = [];
  for (const { code, fields } of records) {
    if (code !== '012') continue;
    const registration = (fields[1] ?? '').trim().toUpperCase();
    if (registration) out.push({ registration, ...typeFromFields(fields) });
  }
  return out;
}

/** "Cessna Grand Caravan": the type name Teslyn Field's TJR import gives an aircraft (brand and model). */
export function tjrTypeName(t: Pick<TjrAircraftType, 'brand' | 'model'>): string {
  return [t.brand, t.model].filter((s) => s !== '').join(' ');
}
